import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import {
  ArrowLeft, ChatCircleDots, Buildings, MapPin, Envelope, DeviceMobile, Phone,
  Lock, Broadcast, CalendarCheck, CheckCircle, ArrowCounterClockwise, XCircle, Paperclip,
  User, Warning, Clock, PaperPlaneTilt,
} from "@phosphor-icons/react";

const STATUSES = [
  { key: "new", label: "New" },
  { key: "reviewing", label: "Reviewing" },
  { key: "scheduled", label: "Scheduled" },
  { key: "waiting_customer", label: "Waiting on Customer" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];
const PRIORITIES = [
  { key: "low", label: "Low" },
  { key: "normal", label: "Normal" },
  { key: "high", label: "High" },
  { key: "urgent", label: "Urgent" },
];
const CATEGORY_LABELS = {
  missed_pickup: "Missed pickup",
  extra_pickup: "Extra pickup request",
  container_issue: "Container issue",
  billing_question: "Billing question",
  account_service_change: "Account / service change",
  other: "Other",
};
const CONTACT_ICON = { email: Envelope, sms: DeviceMobile, phone: Phone };

const EVENT_LABELS = {
  submitted: "Issue submitted",
  assigned: "Assigned",
  reassigned: "Reassigned",
  status_changed: "Status changed",
  priority_changed: "Priority changed",
  internal_note_added: "Internal note added",
  customer_update_published: "Customer-visible update published",
  customer_message_added: "Customer message received",
  attachment_added: "Attachment added",
  scheduled: "Service scheduled",
  resolved: "Resolved",
  reopened: "Reopened",
  closed: "Closed",
  deleted: "Deleted",
};

function formatDT(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function Panel({ title, testid, children, right }) {
  return (
    <section className="rounded-sm bg-white border border-[#012340]/10" data-testid={testid}>
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-[#012340]/10 bg-[#FAFAFA]">
        <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#012340]">{title}</h3>
        {right}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function LabelValue({ label, value, testid, mono }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A]">{label}</div>
      <div className={`text-sm text-[#012340] ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function CustomerIssueDetail() {
  const { iid } = useParams();
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAction, setSavingAction] = useState(null); // key of the in-flight action
  const [noteText, setNoteText] = useState("");
  const [updateText, setUpdateText] = useState("");
  const [updateStatus, setUpdateStatus] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get(`/customer-issues/${iid}`);
      setIssue(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load issue");
    } finally { setLoading(false); }
  }

  async function loadAdmins() {
    try {
      const { data } = await api.get("/users");
      setAdmins((data.users || data || []).filter((u) => u.role === "admin"));
    } catch (e) { /* noop */ }
  }

  useEffect(() => { reload(); loadAdmins(); /* eslint-disable-line */ }, [iid]);

  async function doAction(key, fn) {
    setSavingAction(key);
    try {
      await fn();
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setSavingAction(null);
    }
  }

  const timeline = useMemo(() => {
    if (!issue) return [];
    // Merge activity_history, internal_notes and customer_updates into one sorted timeline.
    const rows = [];
    for (const a of (issue.activity_history || [])) {
      rows.push({ kind: "activity", at: a.at, data: a });
    }
    for (const n of (issue.internal_notes || [])) {
      rows.push({ kind: "internal_note", at: n.at, data: n });
    }
    for (const u of (issue.customer_updates || [])) {
      rows.push({ kind: "customer_update", at: u.created_at, data: u });
    }
    for (const f of (issue.customer_followups || [])) {
      rows.push({ kind: "customer_followup", at: f.at, data: f });
    }
    rows.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
    return rows;
  }, [issue]);

  if (loading || !issue) {
    return (
      <div className="p-8 text-sm text-[#5C738A]" data-testid="ci-detail-loading">Loading issue…</div>
    );
  }

  const ContactIcon = CONTACT_ICON[issue.preferred_contact] || Envelope;
  const canResolve = !["resolved", "closed"].includes(issue.status);
  const canReopen = ["resolved", "closed"].includes(issue.status);
  const canClose = issue.status !== "closed";

  return (
    <div className="px-4 md:px-8 py-6 space-y-5" data-testid="ci-detail-page">
      {/* Header bar */}
      <div className="flex items-center flex-wrap justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/customer-issues")}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-white border border-[#012340]/15 text-xs font-bold uppercase tracking-[0.2em]"
            data-testid="ci-back">
            <ArrowLeft size={14} weight="bold" /> Back
          </button>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#CFAB7A] font-mono" data-testid="ci-pub-id">
              {issue.public_id}
            </div>
            <h1 className="font-heading font-black text-2xl text-[#012340]" data-testid="ci-subject">{issue.subject}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Status</span>
          <select value={issue.status}
            onChange={(e) => doAction("status", async () => {
              await api.patch(`/customer-issues/${iid}/status`, { status: e.target.value });
              toast.success("Status updated");
            })}
            disabled={savingAction === "status"}
            className="px-2 py-1 rounded-sm border border-[#012340]/20 text-xs font-bold"
            data-testid="ci-status-select">
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] ml-2">Priority</span>
          <select value={issue.priority}
            onChange={(e) => doAction("priority", async () => {
              await api.patch(`/customer-issues/${iid}/priority`, { priority: e.target.value });
              toast.success("Priority updated");
            })}
            disabled={savingAction === "priority"}
            className="px-2 py-1 rounded-sm border border-[#012340]/20 text-xs font-bold"
            data-testid="ci-priority-select">
            {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left column — details */}
        <div className="lg:col-span-2 space-y-5">
          <Panel title="Request" testid="ci-request-panel">
            <div className="grid md:grid-cols-2 gap-3">
              <LabelValue label="Confirmation Number" value={issue.public_id} mono testid="ci-req-pubid" />
              <LabelValue label="Category" value={CATEGORY_LABELS[issue.category] || issue.category} />
              <LabelValue label="Submitted" value={formatDT(issue.created_at)} />
              <LabelValue label="Last Updated" value={formatDT(issue.updated_at)} />
              <LabelValue label="Source" value={issue.source === "customer_portal" ? "Customer Portal" : "Manual (staff)"} />
              <LabelValue label="Scheduled" value={issue.scheduled_at ? formatDT(issue.scheduled_at) : "—"} />
            </div>
            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A]">Customer Message</div>
              <div className="mt-1 whitespace-pre-wrap text-sm text-[#012340] bg-[#FAFAFA] border border-[#012340]/10 rounded-sm p-3" data-testid="ci-message-body">
                {issue.message}
              </div>
            </div>
          </Panel>

          <Panel title="Activity & Notes" testid="ci-activity-panel">
            {/* Add internal note */}
            <div className="mb-4">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1.5">
                <Lock size={11} weight="bold" /> Internal Note (staff-only, never sent to customer)
              </div>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add an internal note — visible only inside the Hub."
                className="w-full min-h-[72px] px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A]"
                data-testid="ci-internal-note-input"
              />
              <button
                onClick={() => doAction("internal_note", async () => {
                  await api.post(`/customer-issues/${iid}/internal-notes`, { body: noteText });
                  setNoteText(""); toast.success("Note saved");
                })}
                disabled={!noteText.trim() || savingAction === "internal_note"}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40"
                data-testid="ci-add-internal-note">
                <Lock size={12} weight="bold" /> Save Internal Note
              </button>
            </div>

            {/* Add customer-visible update */}
            <div className="mb-4 pt-4 border-t border-[#012340]/10">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1.5">
                <Broadcast size={11} weight="bold" /> Customer-Visible Update (sent to the Portal)
              </div>
              <textarea
                value={updateText}
                onChange={(e) => setUpdateText(e.target.value)}
                placeholder="Write a customer-safe update. Never paste internal notes here."
                className="w-full min-h-[72px] px-3 py-2 text-sm rounded-sm border border-[#012340]/20 focus:outline-none focus:border-[#CFAB7A]"
                data-testid="ci-customer-update-input"
              />
              <div className="mt-2 flex items-center gap-2">
                <select value={updateStatus} onChange={(e) => setUpdateStatus(e.target.value)}
                  className="px-2 py-1 text-xs rounded-sm border border-[#012340]/20"
                  data-testid="ci-customer-update-status">
                  <option value="">Keep current status</option>
                  {STATUSES.map((s) => <option key={s.key} value={s.key}>Set → {s.label}</option>)}
                </select>
                <button
                  onClick={() => doAction("customer_update", async () => {
                    const body = { message: updateText };
                    if (updateStatus) body.customer_facing_status = updateStatus;
                    await api.post(`/customer-issues/${iid}/updates`, body);
                    setUpdateText(""); setUpdateStatus("");
                    toast.success("Customer update published");
                  })}
                  disabled={!updateText.trim() || savingAction === "customer_update"}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40"
                  data-testid="ci-add-customer-update">
                  <PaperPlaneTilt size={12} weight="bold" /> Publish Update
                </button>
              </div>
            </div>

            {/* Timeline */}
            <div className="pt-4 border-t border-[#012340]/10">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-2">Timeline</div>
              <ol className="space-y-3" data-testid="ci-timeline">
                {timeline.length === 0 && <li className="text-sm text-[#5C738A]">No activity yet.</li>}
                {timeline.map((row, idx) => (
                  <TimelineEntry key={idx} row={row} />
                ))}
              </ol>
            </div>
          </Panel>
        </div>

        {/* Right column — customer + service + actions */}
        <div className="space-y-5">
          <Panel title="Customer" testid="ci-customer-panel">
            <LabelValue label="Customer / Business" value={issue.customer_name} testid="ci-customer-name" />
            <LabelValue label="Preferred Contact" value={
              <span className="inline-flex items-center gap-1"><ContactIcon size={12} weight="bold" /> {issue.preferred_contact || "email"}</span>
            } />
            <LabelValue label="Email (masked)" value={issue.customer_email_masked || "—"} mono testid="ci-customer-email-masked" />
            <LabelValue label="Phone (masked)" value={issue.customer_phone_masked || "—"} mono />
            <LabelValue label="Zoho Customer ID" value={issue.zoho_customer_id || "—"} mono testid="ci-zoho-id" />
          </Panel>

          <Panel title="Service" testid="ci-service-panel">
            <LabelValue label="Service Location" value={issue.service_location} />
            <LabelValue label="Container Size" value={issue.container_size} />
            <LabelValue label="Frequency" value={issue.frequency} />
          </Panel>

          <Panel title="Assignment & Actions" testid="ci-actions-panel">
            <LabelValue label="Assigned To" value={issue.assigned_to_name || "Unassigned"} testid="ci-assigned-to" />
            <div className="flex items-center gap-2 mb-3">
              <select
                value={issue.assigned_to_user_id || ""}
                onChange={(e) => doAction("assign", async () => {
                  await api.patch(`/customer-issues/${iid}/assign`, { user_id: e.target.value || null });
                  toast.success("Assignee updated");
                })}
                disabled={savingAction === "assign"}
                className="flex-1 px-2 py-1.5 rounded-sm border border-[#012340]/20 text-sm"
                data-testid="ci-assign-select">
                <option value="">— Unassigned —</option>
                {admins.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div className="pt-3 border-t border-[#012340]/10 space-y-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A]">Quick actions</div>
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={() => {
                    const iso = prompt("Schedule datetime (ISO, e.g. 2026-02-15T09:00:00Z):");
                    if (iso) doAction("schedule", async () => {
                      await api.patch(`/customer-issues/${iid}/schedule`, { scheduled_at: iso });
                      toast.success("Service scheduled");
                    });
                  }}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-white border border-[#012340]/15 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#F5F5F5]"
                  data-testid="ci-schedule-btn">
                  <CalendarCheck size={12} weight="bold" /> Schedule Service
                </button>
                <button
                  disabled={!canResolve}
                  onClick={() => doAction("resolve", async () => {
                    await api.patch(`/customer-issues/${iid}/status`, { status: "resolved" });
                    toast.success("Marked resolved");
                  })}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-[#2E7D32] text-white text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40"
                  data-testid="ci-resolve-btn">
                  <CheckCircle size={12} weight="bold" /> Mark Resolved
                </button>
                <button
                  disabled={!canReopen}
                  onClick={() => doAction("reopen", async () => {
                    await api.patch(`/customer-issues/${iid}/status`, { status: "new" });
                    toast.success("Issue reopened");
                  })}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-white border border-[#CFAB7A] text-[#7A5A18] text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40"
                  data-testid="ci-reopen-btn">
                  <ArrowCounterClockwise size={12} weight="bold" /> Reopen
                </button>
                <button
                  disabled={!canClose}
                  onClick={() => doAction("close", async () => {
                    await api.patch(`/customer-issues/${iid}/status`, { status: "closed" });
                    toast.success("Issue closed");
                  })}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm bg-[#37474F] text-white text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40"
                  data-testid="ci-close-btn">
                  <XCircle size={12} weight="bold" /> Close Issue
                </button>
              </div>
            </div>
          </Panel>

          <Panel title="Attachments" testid="ci-attachments-panel">
            <ul className="space-y-1" data-testid="ci-attachments-list">
              {(issue.attachments || []).length === 0 && (
                <li className="text-xs text-[#5C738A]">No attachments.</li>
              )}
              {(issue.attachments || []).map((a) => (
                <li key={a.id} className="flex items-center justify-between text-xs" data-testid={`ci-attachment-${a.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip size={12} weight="bold" className="text-[#5C738A]" />
                    <span className="truncate">{a.filename}</span>
                    <span className="text-[#5C738A]">{(a.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C738A]">{a.uploaded_by}</span>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function TimelineEntry({ row }) {
  const { kind, data } = row;
  if (kind === "internal_note") {
    return (
      <li className="flex gap-3" data-testid="tl-internal-note">
        <span className="mt-0.5 w-7 h-7 grid place-items-center rounded-full bg-[#012340]/10 text-[#012340]"><Lock size={12} weight="bold" /></span>
        <div className="flex-1">
          <div className="text-xs text-[#5C738A]">
            <span className="font-bold text-[#012340]">Internal note</span> · {data.by_user_name || "staff"} · {formatDT(data.at)}
          </div>
          <div className="mt-0.5 text-sm text-[#012340] whitespace-pre-wrap bg-[#FAFAFA] border border-[#012340]/10 rounded-sm p-2">{data.body}</div>
        </div>
      </li>
    );
  }
  if (kind === "customer_update") {
    return (
      <li className="flex gap-3" data-testid="tl-customer-update">
        <span className="mt-0.5 w-7 h-7 grid place-items-center rounded-full bg-[#CFAB7A]/25 text-[#7A5A18]"><Broadcast size={12} weight="bold" /></span>
        <div className="flex-1">
          <div className="text-xs text-[#5C738A]">
            <span className="font-bold text-[#7A5A18]">Customer-visible update</span> · {data.public_id} · {data.by_user_name || "staff"} · {formatDT(data.created_at)}
          </div>
          <div className="mt-0.5 text-sm text-[#012340] whitespace-pre-wrap bg-[#FFF9E6] border border-[#CFAB7A]/40 rounded-sm p-2">{data.message}</div>
          {data.customer_facing_status && (
            <div className="text-[10px] mt-1 font-bold uppercase tracking-[0.18em] text-[#7A5A18]">Status → {data.customer_facing_status}</div>
          )}
        </div>
      </li>
    );
  }
  if (kind === "customer_followup") {
    return (
      <li className="flex gap-3" data-testid="tl-customer-followup">
        <span className="mt-0.5 w-7 h-7 grid place-items-center rounded-full bg-[#E3F2FD] text-[#0D47A1]"><User size={12} weight="bold" /></span>
        <div className="flex-1">
          <div className="text-xs text-[#5C738A]">
            <span className="font-bold text-[#0D47A1]">Customer follow-up</span> · via Portal · {formatDT(data.at)}
          </div>
          <div className="mt-0.5 text-sm text-[#012340] whitespace-pre-wrap bg-[#E3F2FD] border border-[#0D47A1]/20 rounded-sm p-2">{data.message}</div>
        </div>
      </li>
    );
  }
  // Activity event
  const label = EVENT_LABELS[data.event] || data.event;
  return (
    <li className="flex gap-3" data-testid={`tl-activity-${data.event}`}>
      <span className="mt-0.5 w-7 h-7 grid place-items-center rounded-full bg-[#F5F5F5] text-[#012340]"><Clock size={12} weight="bold" /></span>
      <div className="flex-1">
        <div className="text-xs text-[#5C738A]">
          <span className="font-bold text-[#012340]">{label}</span>
          {data.actor_name ? <> · {data.actor_name}</> : (data.actor_type === "portal" ? <> · via Portal</> : null)}
          <> · {formatDT(data.at)}</>
        </div>
        {(data.prev || data.new) && (
          <div className="text-[11px] text-[#5C738A] mt-0.5">
            {data.prev ? <>from <code className="bg-[#F5F5F5] px-1">{String(data.prev)}</code> </> : null}
            {data.new ? <>→ <code className="bg-[#F5F5F5] px-1">{String(data.new)}</code></> : null}
          </div>
        )}
      </div>
    </li>
  );
}
